import type {ApolloCache} from '@apollo/client';

/**
 * Rewrites the cached `Transaction.logo` of every transaction with a given
 * payee, after that payee's logo assignment changes.
 *
 * Necessary because `Transaction.logo` is derived server-side from the payee
 * rather than stored on the transaction, so Apollo has no way to know a
 * `setPayeeLogo` mutation invalidated it. Refetching `PayeesWithLogos` alone
 * only fixes the settings page; `TransactionListView` reads cache-first on POP
 * navigation, so pressing Back after assigning a logo would show every affected
 * row with the old one and never hit the network.
 *
 * Writing the new value rather than evicting the field keeps those rows from
 * going incomplete and triggering a refetch — the correct value is already
 * known here.
 */
const updateTransactionLogos = (
  cache: ApolloCache<object>,
  payee: string,
  logo: string | null,
) => {
  // No public API enumerates normalized objects of a type, so read the store
  // and pick out Transaction entries by their cache id prefix.
  const ids = Object.keys(cache.extract()).filter((id) =>
    id.startsWith('Transaction:'),
  );

  for (const id of ids) {
    cache.modify({
      id,
      fields: {
        logo: (existing: string | null, {readField}) =>
          readField<string>('payee') === payee ? logo : existing,
      },
    });
  }
};

export default updateTransactionLogos;
